import React, { useMemo } from "react";
import { useAppSelector } from "../../../hooks/hooks";

import {
  Container,
  Text,
  LoadingPlaceholder,
  Icon,
  IconList,
  StaticMap,
} from "@kidslivesafe/sc-react";

import { selectMapboxKey } from "../../../features/brand/brandSlice";

import { selectPhoneReportStatus } from "../../../features/reports/reportsSlice";

import { PhoneAddress } from "../../../features/reports/reportTypes";

import { useStaticMapHook } from "../../../hooks/staticMapHook";
interface PinData {
  latitude: number;
  longitude: number;
  popup: React.ReactNode;
  element?: React.ReactNode;
}

const PossibleLocations: React.FC<{
  sectionId: string;
  addresses: PhoneAddress[];
  printReady?: boolean;
}> = ({ sectionId, addresses, printReady }) => {
  const mapBoxKey = useAppSelector(selectMapboxKey);
  const phoneReportStatus = useAppSelector(selectPhoneReportStatus);
  const loading = phoneReportStatus === "loading";

  // remove local state/effect; compute pins from addresses
  const phoneMapPins = useMemo<PinData[]>(() => {
    const grouped: Record<string, PhoneAddress[]> = {};
    (addresses || []).forEach(a => {
      if (a.latitude == null || a.longitude == null) return;
      const key = `${a.latitude},${a.longitude}`;
      (grouped[key] ||= []).push(a);
    });
    return Object.values(grouped).map(group => {
      const { latitude, longitude } = group[0];
      return {
        latitude,
        longitude,
        popup: (
          <>
            {group.map((address, index) => (
              <div key={index}>
                <Text fontWeight="bold" size="sm">
                  {address.name}
                </Text>
              </div>
            ))}
            <Text size="xxs">
              {group[0].fullAddress}, {group[0].city}, {group[0].state}{" "}
              {group[0].postalCode}
            </Text>
          </>
        ),
      };
    });
  }, [addresses]);

  const { containerRef, detailsSpacing, mapLoading } = useStaticMapHook({
    loading,
  });

  return (
    <>
      {((addresses && addresses.length !== 0) || loading) && !printReady ? (
        <Container
          containerTitle={
            loading
              ? "Possible Locations"
              : `Possible Locations (${addresses.length > 0 ? addresses.length : 0})`
          }
          tooltipContent="Locations are included for the registration of the phone itself as well as any persons or businesses found to be associated with the phone record.  See more detail within the Business and People results sections below."
          containerId={sectionId}
        >
          <div ref={containerRef} style={{ margin: "0 auto", width: "100%" }}>
            {loading || mapLoading || !detailsSpacing ? (
              <LoadingPlaceholder width="100%" height="500px" />
            ) : phoneMapPins.length > 0 ? (
              <StaticMap
                mapboxToken={mapBoxKey}
                markers={phoneMapPins}
                width={detailsSpacing.w}
                height={detailsSpacing.h}
                paddingPx={40}
                renderMarker={() => (
                  <div style={{ transform: "translateY(-10px)" }}>
                    <Icon
                      width={16}
                      height={20}
                      className="sc-map-marker__icon--primary"
                      name={IconList.IconPin}
                    />
                  </div>
                )}
              />
            ) : null}
          </div>
        </Container>
      ) : null}
    </>
  );
};

export default PossibleLocations;
